import site from './site.json'

export { site }

/** Joins the site origin with a path, avoiding a double slash. */
export const absoluteUrl = (path = '/') =>
  `${site.siteUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`

/** Metadata for one of the static marketing routes, if we have any. */
export const routeMeta = (pathname) => site.routes[pathname] || null

/** Comma-joined service-area list, e.g. for weaving into a description. */
export const areaList = (limit = 5) =>
  site.business.areaServed.slice(0, limit).join(', ')

/**
 * LocalBusiness structured data. This is what Google reads for local results,
 * so every field here has to be verifiable — no invented address or phone.
 * Fields left blank in site.json are omitted rather than guessed.
 */
export const localBusinessSchema = () => {
  const { business } = site

  const address = {
    '@type': 'PostalAddress',
    addressLocality: business.locality,
    addressRegion: business.region,
    addressCountry: business.country
  }
  if (business.streetAddress) address.streetAddress = business.streetAddress
  if (business.postalCode) address.postalCode = business.postalCode

  const schema = {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'HealthAndBeautyBusiness'],
    '@id': `${absoluteUrl('/')}#business`,
    name: business.legalName,
    description: site.routes['/'].description,
    url: absoluteUrl('/'),
    image: absoluteUrl(site.socialImage),
    address,
    areaServed: business.areaServed.map((name) => ({ '@type': 'Place', name })),
    founder: {
      '@type': 'Person',
      name: business.trainer,
      jobTitle: 'Personal Trainer',
      knowsAbout: business.services
    },
    makesOffer: business.services.map((service) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: service }
    }))
  }

  if (business.telephone) schema.telephone = business.telephone

  return schema
}

/** Breadcrumbs help Google show the site structure under a result. */
export const breadcrumbSchema = (trail) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: trail.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: absoluteUrl(crumb.path)
  }))
})

/** Marks up a single exercise page as a how-to style video resource. */
export const exerciseSchema = (exercise) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ExerciseAction',
    name: exercise.title,
    description: exercise.description,
    url: absoluteUrl(`/exercises/${exercise.id}`)
  }
  if (exercise.category) schema.exerciseType = exercise.category
  return schema
}
